import { createColumnHelper, flexRender, Header, useTable } from "@tanstack/react-table"
import { Badge } from "design-system/atoms/Badge"
import { Checkbox } from "design-system/atoms/Checkbox"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { features, type Features } from "lib/table"
import { capitalize } from "lopermut"
import type { useWhatsappTemplatesQuery } from "modules/campaigns/hooks/sections/whatsapp/useWhatsappTemplatesQuery"
import Locale from "modules/shared/components/Miscelanea/Locale"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { WhatsappTemplatesResponseDataSchema, WhatsappTemplatesSortSchema } from "schema"
import { z } from "zod"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof WhatsappTemplatesResponseDataSchema>[number]
>()

type WhatsappTemplatesSortFields = z.infer<typeof WhatsappTemplatesSortSchema>["sortBy"]["field"]

type Props = {
  data: ReturnType<typeof useWhatsappTemplatesQuery>["data"]
  isPending: boolean
  selectedIds?: string[]
  initialSelectedIds?: string[]
  onSelectionChange?: (ids: string[]) => void
  fields: WhatsappTemplatesSortFields[]
  onSort: (field: WhatsappTemplatesSortFields) => void
  search: {
    field: WhatsappTemplatesSortFields
    order: "asc" | "desc"
  }
}

const CellMedia = ({
  url,
  format,
}: {
  url?: string | null
  format?: z.infer<typeof WhatsappTemplatesResponseDataSchema>[number]["mediaFormat"]
}) => {
  if (url && format) {
    switch (format) {
      case "IMAGE":
      case "GIF": {
        return (
          <Tooltip>
            <TooltipTrigger>
              <img src={url} className="max-h-6 w-auto rounded-sm" />
            </TooltipTrigger>
            <TooltipContent className="p-2">
              <img src={url} className="max-h-[200px] w-auto rounded-sm" />
            </TooltipContent>
          </Tooltip>
        )
      }

      default:
        return null
    }
  }
}

export const WhatsappTemplatesTable = ({
  data,
  isPending,
  selectedIds,
  initialSelectedIds,
  onSelectionChange,
  fields,
  onSort,
  search,
}: Props) => {
  const { t } = useTranslation()

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("name", {
          header: t("campaigns.templates.whatsapp.table.name"),
          cell: ({ row }) => {
            const url = row.original.mediaUrl
            const format = row.original.mediaFormat

            if (!onSelectionChange)
              return (
                <div className="flex justify-between">
                  <span className="font-mono truncate max-w-[200px]">{row.original.name}</span>
                  <CellMedia format={format} url={url} />
                </div>
              )

            const isInitiallySelected = initialSelectedIds?.includes(row.original.id)

            return (
              <div className="flex items-center gap-4">
                <Checkbox
                  checked={selectedIds?.includes(row.original.id)}
                  onCheckedChange={(checked) => {
                    // If initially selected, only allow checking, not unchecking
                    if (isInitiallySelected && !checked) return

                    const newSelectedIds = checked
                      ? [...(selectedIds ?? []), row.original.id]
                      : (selectedIds ?? []).filter((id) => id !== row.original.id)
                    onSelectionChange(newSelectedIds)
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                  aria-label="Select row"
                />
                <span className="font-mono truncate max-w-[200px]">{row.original.name}</span>
                <CellMedia format={format} url={url} />
              </div>
            )
          },
          meta: {
            colSpan: 2,
          },
        }),
        columnHelper.accessor("category", {
          header: t("campaigns.templates.whatsapp.table.category"),
          cell: ({ row }) => {
            return (
              <Badge variant="secondary" key="trigger-value">
                {capitalize(row.original.category)}
              </Badge>
            )
          },
        }),
        columnHelper.accessor("internalId", {
          header: t("campaigns.templates.whatsapp.table.whatsappTemplateId"),
          cell: ({ row }) => {
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-mono">{row.original.internalId}</span>
                </TooltipTrigger>
                <TooltipContent>
                  {t("campaigns.templates.whatsapp.table.whatsappTemplateTooltip")}
                </TooltipContent>
              </Tooltip>
            )
          },
        }),
        columnHelper.accessor("language", {
          header: t("campaigns.templates.whatsapp.table.language"),
          cell: ({ getValue }) => {
            const value = getValue()
            return value ? <Locale code={value} /> : "-"
          },
        }),
        columnHelper.accessor("status", {
          header: t("campaigns.templates.whatsapp.table.status"),
          cell: (props) => {
            const status = props.getValue()
            let variant: "success" | "warning" | "negative" = "warning"
            if (status === "APPROVED") variant = "success"
            if (status === "REJECTED" || status === "PENDING_DELETION") variant = "negative"

            return (
              <Badge variant={variant}>{t(`campaigns.templates.whatsapp.status.${status}`)}</Badge>
            )
          },
        }),
        columnHelper.accessor("isOneTime", {
          header: t("campaigns.templates.whatsapp.table.isOneTime"),
          cell: ({ getValue }) =>
            t(`campaigns.templates.whatsapp.table.${getValue() ? "yes" : "no"}`),
        }),
      ]),
    [t, selectedIds, onSelectionChange, initialSelectedIds],
  )

  const table = useTable({
    data: data ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("campaigns.templates.whatsapp.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.original.id}
        navigate={{
          to: "/settings/channels/whatsapp/templates/$id",
          params: { id: row.original.id },
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={cn("cursor-pointer", cell.column.columnDef.meta?.className)}
            {...cell.column.columnDef.meta}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  function buildSortable(
    header: Header<Features, z.infer<typeof WhatsappTemplatesResponseDataSchema>[number], unknown>,
  ) {
    if (!fields.includes(header.id as WhatsappTemplatesSortFields)) return

    const isActive = search.field === header.id

    return {
      active: isActive,
      direction: isActive ? search.order : "asc",
      onClick: () => onSort(header.id as WhatsappTemplatesSortFields),
    }
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                sortable={buildSortable(header)}
                {...header.column.columnDef.meta}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody className="**:data-[slot=table-cell]:first:w-8">{renderContent()}</TableBody>
    </Table>
  )
}
